/**
 * Search-facing how-to for an inbound AI voice agent.
 *
 * flogpt.com only. The India build (`VITE_SITE_REGION=in`) returns null
 * before any of the article strings, the same way US calling citations stay
 * out of the flogpt.in bundle. On flogpt.in the URL is not prerendered,
 * noindex, and not in the .in sitemap. nginx `~^/blog/.+` still serves the
 * app shell for that path.
 *
 * Pricing lines are the flogpt.com/pricing wording:
 * "Free (100 messages/month), paid plans from $49/month",
 * "+ usage for calls and SMS", and
 * "Voice agents and outbound triggers require a paid plan".
 * FAQ 7 folds the usage phrase into the price sentence ("plus usage for calls and SMS").
 */

/**
 * Merge day. `datePublished`, `dateModified`, and the visible Updated line
 * all read this. Change this one value when the guide merges.
 */
export const GUIDE_UPDATED_ON = "2026-10-27";

const GUIDE_UPDATED_MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
] as const;

/** "October 27, 2026" from `GUIDE_UPDATED_ON`. */
export function guideUpdatedLabel(iso: string = GUIDE_UPDATED_ON): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) throw new Error(`Guide date must be YYYY-MM-DD, got ${iso}`);
    const [, year, month, day] = match;
    const monthName = GUIDE_UPDATED_MONTHS[Number(month) - 1];
    if (!monthName) throw new Error(`Guide date must be YYYY-MM-DD, got ${iso}`);
    return `${monthName} ${Number(day)}, ${year}`;
}

export const INBOUND_VOICE_GUIDE_PATH = "/blog/how-to-set-up-ai-voice-agent-inbound-calls";

/** Overview post that should point at this guide from its inbound section. */
export const INBOUND_VOICE_OVERVIEW_SLUG = "ai-voice-agents-for-smarter-inbound-and-outbound-calling";

const VOICE_FAQ_QUESTION = "How does an AI voice agent answer phone calls?";

export type InboundGuideLink = { href: string; label: string };

/** Null on the India build so shared pages do not link at a page flogpt.in does not publish. */
export function comOnlyInboundGuideLink(): InboundGuideLink | null {
    if (import.meta.env.VITE_SITE_REGION === "in") return null;
    return { href: INBOUND_VOICE_GUIDE_PATH, label: "Read the setup guide" };
}

export function withInboundSetupGuideLink<T extends { question: string }>(items: readonly T[]): Array<T & { link?: InboundGuideLink }> {
    const link = comOnlyInboundGuideLink();
    if (!link) return items.map((item) => ({ ...item }));
    return items.map((item) => (item.question === VOICE_FAQ_QUESTION ? { ...item, link } : { ...item }));
}

export type GuideBlock =
    | { type: "h2" | "h3" | "p"; text: string }
    | { type: "ul"; items: string[] };

export type InboundVoiceGuide = {
    path: string;
    seoTitle: string;
    description: string;
    keywords: string;
    h1: string;
    updatedLabel: string;
    datePublished: string;
    dateModified: string;
    lede: string;
    disclosure: string;
    blocks: GuideBlock[];
    howToSteps: Array<{ position: number; name: string; text: string }>;
    faqs: Array<{ question: string; answer: string }>;
    legal: string;
};

let cached: InboundVoiceGuide | undefined;

export function inboundVoiceGuideForBuild(): InboundVoiceGuide | null {
    if (import.meta.env.VITE_SITE_REGION === "in") return null;
    if (cached) return cached;

    cached = {
        path: INBOUND_VOICE_GUIDE_PATH,
        seoTitle: "How to Set Up an AI Voice Agent for Inbound Calls · FloGPT",
        description:
            "Set up an AI voice agent to answer inbound calls in 5 steps: pick a use case, write the prompt, connect a number, test, then add transfers and a pipeline.",
        keywords:
            "how can i set up an ai voice bot to handle inbound calls, inbound call center automation, ai inbound call agent, voice ai for automated inbound calls",
        h1: "How to set up an AI voice agent to answer inbound calls",
        updatedLabel: guideUpdatedLabel(),
        datePublished: GUIDE_UPDATED_ON,
        dateModified: GUIDE_UPDATED_ON,
        lede: "To set up an AI voice agent for inbound calls, ground it in a knowledge base first, then pick a use-case template, write a short greeting and a 100–500-word system prompt, connect a phone number, and make a test call. Read the first transcripts, then add human-transfer rules and a branching call pipeline.",
        disclosure:
            "FloGPT is our product. The steps below follow FloGPT's own Guided Setup ([AI Calls quick start](/docs/get-started/ai-calls)); other voice-agent tools use different screens.",
        blocks: [
            { type: "h2", text: "What does an inbound AI voice agent do?" },
            {
                type: "p",
                text: "It answers your phone number and holds a real conversation instead of reading a menu. A FloGPT voice agent answers calls on a number you buy in-app or already own, uses your knowledge base to answer questions, and can book appointments, qualify leads, look up order status, send follow-up SMS and transfer the caller to a person.",
            },
            {
                type: "p",
                text: "That's the difference from a press-1 IVR. A traditional IVR follows a fixed menu tree; a voice agent understands free-form speech, looks up live data such as orders, calendars and CRM records, and handles multi-turn conversations without menu trees to maintain.",
            },
            { type: "h2", text: "What do you need before you start?" },
            {
                type: "p",
                text: "You need four things: a knowledge base, a phone number, a list of the calls you want handled, and someone to take transfers.",
            },
            {
                type: "ul",
                items: [
                    '**A knowledge base.** FloGPT\'s docs put it plainly: "a voice agent with no knowledge base can only follow its prompt, and that is a much weaker agent." Start with the [Start here](/docs/get-started/start-here) guide.',
                    "**A phone number,** or a plan to get one. You can link one you already have, buy one, request one, or attach one later.",
                    "**The calls you want the agent to take,** for example common questions, appointment booking, or after-hours calls.",
                    "**A person or team for handovers,** and the rules for when the agent should hand over.",
                ],
            },
            { type: "h2", text: "How to set up an inbound AI voice agent in 5 steps" },
            {
                type: "p",
                text: "In FloGPT, go to **AI Calls → Call Widgets** and press **Guided Setup**. The wizard walks through five steps.",
            },
            { type: "h3", text: "Step 1: Pick a use case" },
            {
                type: "p",
                text: "Choose the blueprint closest to your calls: customer support, appointment booking, lead generation, outbound sales, survey and feedback, ads-to-call, or a blank agent. The blueprint pre-fills the prompt and voice settings and decides which later steps you see; you can change all of it.",
            },
            { type: "h3", text: "Step 2: Name the agent and pick language and country" },
            {
                type: "p",
                text: 'Give the widget an internal name you\'ll recognise (for example, "Front desk — inbound"), choose the primary language, and set the operating country. The country decides how you get a number: the United States, Canada, United Kingdom, Australia, Ireland and New Zealand are self-serve, while India and other markets use manual provisioning, which an admin reviews within one business day.',
            },
            { type: "h3", text: "Step 3: Write the greeting and the system prompt" },
            {
                type: "p",
                text: "Keep the welcome message under 15 seconds, and aim for a system prompt of 100 to 500 words. The docs call one edit the most valuable: tell the agent what it must never promise, quote or diagnose, and when it should hand over to a person instead.",
            },
            { type: "h3", text: "Step 4: Connect a phone number" },
            {
                type: "p",
                text: "Pick one of four options: link an existing number, buy a number (self-serve countries only), request a number from the ops team (the path for India and other manual-provisioning markets), or skip for now and attach one later from **AI Calls → Phone Numbers**. Each number has two slots, one inbound widget that answers calls and one outbound widget, so one number can host both.",
            },
            { type: "h3", text: "Step 5: Finish setup and make a test call" },
            {
                type: "p",
                text: 'Work through the **Finish setup** checklist, then call the number yourself. The widget\'s **Active** toggle stays locked until a phone number is linked, so check that first if the agent "seems dead". Then open **AI Calls → Call Logs** to see the call\'s direction, status, duration and sentiment, read the transcript and hear the recording.',
            },
            { type: "h2", text: "How do you hand a call over to a human?" },
            {
                type: "p",
                text: "Set transfer rules. A FloGPT voice agent can warm-transfer to a person with a spoken intro and a conversation summary, cold-transfer to a number, or escalate by SMS to a specific team member. You configure the rules per intent, per time of day or per caller attribute.",
            },
            {
                type: "p",
                text: "If people on your team also take calls, they work in **Call Centre** (dialer, lead queue, inbound handling), which is separate from AI Calls. See the [Call Centre guide](/docs/get-started/call-centre).",
            },
            { type: "h2", text: "How do you go beyond a single prompt?" },
            {
                type: "p",
                text: "Build a call pipeline. FloGPT's [Call Pipeline](/ai-call-pipeline) is a visual editor where you drag nodes to set how the agent greets, listens, looks things up, takes actions, and ends or transfers. It can:",
            },
            {
                type: "ul",
                items: [
                    "Branch by intent (for example, booking versus cancelling) and capture typed fields.",
                    "Treat knowledge base lookups, calendars, Shopify and your own tools as nodes.",
                    "Book or reschedule appointments with Google or Microsoft calendars synced both ways, and send SMS.",
                    "Start from a vertical pack (Dental, Real Estate, Retail, Support) or from scratch.",
                    "Version every publish, with one-click rollback.",
                ],
            },
            { type: "h2", text: "What should you automate first in an inbound call center?" },
            {
                type: "p",
                text: "Start with repeatable, low-risk calls, then widen the scope as you read transcripts. The calls FloGPT's agent is built to handle include:",
            },
            {
                type: "ul",
                items: [
                    "Answers to common questions from your knowledge base",
                    "Order status and account lookups",
                    "Booking, rescheduling and confirming appointments",
                    "Qualifying inbound leads",
                    "After-hours overflow and quote requests",
                ],
            },
            {
                type: "p",
                text: "Keep anything sensitive, or anything the prompt says the agent must never answer, as a transfer to a person.",
            },
            { type: "h2", text: "What mistakes should you avoid?" },
            {
                type: "ul",
                items: [
                    "**No knowledge base.** The agent can only follow its prompt.",
                    '**No "never" rules in the prompt.** Spell out what it must never promise, quote or diagnose, and when to hand over.',
                    "**Not reading transcripts.** The docs say reading the first three teaches you more about your prompt than editing it in the abstract.",
                    "**No linked number.** The Active toggle stays locked until one is attached.",
                    "**Ignoring privacy settings.** If you handle sensitive data, set transcript retention (up to 90 days) and turn on PII redaction in [Setup](/docs/get-started/setup).",
                ],
            },
            { type: "h2", text: "Inbound vs outbound AI calls" },
            {
                type: "p",
                text: 'Inbound agents answer calls that come to you. Outbound agents place calls from a list, a trigger or a campaign, and FloGPT\'s docs note that outbound calling is regulated and that FloGPT "gives you controls rather than automatic compliance". Read the outbound responsibilities in the docs before you dial out.',
            },
            { type: "h2", text: "How FloGPT helps" },
            {
                type: "ul",
                items: [
                    "**Guided Setup** for voice agents, with use-case blueprints.",
                    "**Call Pipeline** with vertical packs, versioning and rollback.",
                    "**Live human transfer** (warm, cold or SMS escalation), plus a separate **Call Centre** for human reps.",
                    "**16 voice languages,** detected automatically ([see the list](/ai-voice-agents)).",
                    "**Pricing:** Free (100 messages/month), paid plans from $49/month. [+ usage for calls and SMS](/pricing#usage-pricing). Voice agents and outbound triggers require a paid plan ([see plans](/pricing)).",
                ],
            },
        ],
        howToSteps: [
            {
                position: 1,
                name: "Pick a use case",
                text: "Choose the blueprint closest to your calls: customer support, appointment booking, lead generation, outbound sales, survey and feedback, ads-to-call, or a blank agent.",
            },
            {
                position: 2,
                name: "Name the agent and pick language and country",
                text: "Give the widget an internal name, choose the primary language, and set the operating country, which decides how you get a phone number.",
            },
            {
                position: 3,
                name: "Write the greeting and the system prompt",
                text: "Keep the welcome message under 15 seconds and aim for a 100 to 500 word system prompt that says what the agent must never promise, quote or diagnose, and when to hand over.",
            },
            {
                position: 4,
                name: "Connect a phone number",
                text: "Link an existing number, buy one in self-serve countries, request one, or skip and attach one later from AI Calls, Phone Numbers.",
            },
            {
                position: 5,
                name: "Finish setup and make a test call",
                text: "Complete the Finish setup checklist, call the number yourself, then read the transcript in Call Logs. The Active toggle stays locked until a number is linked.",
            },
        ],
        faqs: [
            {
                question: "Do I need a new phone number for an AI voice agent?",
                answer: "No. In FloGPT you can link a number you already have on the workspace, buy one in self-serve countries, request one from the ops team, or skip and attach one later. FloGPT also supports bringing your own number or porting one. Each number has an inbound and an outbound slot, so one number can host both agents.",
            },
            {
                question: "What should an AI voice agent's system prompt include?",
                answer: "FloGPT's docs suggest 100 to 500 words. Describe the job, the tone and the information the agent can use, then add the most valuable part: what it must never promise, quote or diagnose, and exactly when it should hand the caller over to a person. Keep the spoken greeting under 15 seconds.",
            },
            {
                question: "Can an AI voice agent transfer a call to a human?",
                answer: "Yes. A FloGPT agent can warm-transfer with a spoken intro and conversation summary, cold-transfer to a number, or escalate by SMS to a specific team member. You set transfer rules per intent, per time of day or per caller attribute, so urgent or sensitive calls reach a person.",
            },
            {
                question: "What languages can an inbound AI voice agent speak?",
                answer: "FloGPT voice agents support 16 languages, including English variants for the US, UK, India, Australia, Canada, Ireland and New Zealand. The agent detects the caller's language automatically and can switch languages mid-call if asked. The full list is on the FloGPT voice agents page.",
            },
            {
                question: "How is an AI voice agent different from an IVR?",
                answer: "A traditional IVR makes callers press keys through a fixed menu tree. An AI voice agent understands free-form speech, looks up live data such as orders, calendars and CRM records, books appointments directly, and handles multi-turn conversations, with no menu trees to maintain. It can still hand over to a person.",
            },
            {
                question: "How do I check what the AI agent said on a call?",
                answer: "Open Call Logs. Each call shows who called, which agent answered, direction, status, duration and sentiment, with a transcript and recording. You can keep transcripts for up to 90 days and turn on PII redaction, which scrubs details like card and phone numbers from transcripts before AI enrichment.",
            },
            {
                question: "How much does an inbound AI voice agent cost on FloGPT?",
                answer: "FloGPT pricing is Free (100 messages/month), paid plans from $49/month, plus usage for calls and SMS. Voice agents and outbound triggers require a paid plan. Current plan details and usage rates are on the FloGPT pricing page.",
            },
        ],
        legal: "FloGPT is a product of Pulse Real LLC, Sheridan, Wyoming, USA.",
    };
    return cached;
}
